/**
 * Jusqu'où on peut faire glisser la carte (ticket 05 de retours-testeurs) : du calcul, pas de
 * JSX, pas d'état.
 *
 * La règle (décision d'Igor du 2026-09-26) : le bord des bulles visibles, étapes ouvertes sur
 * place comprises, ne quitte jamais tout à fait la surface. On peut tirer un peu au-delà, avec
 * une résistance, comme sur un téléphone ; au lâcher, la carte revient seule (`cameraCarte.ts`).
 * Rien ne dessine la limite : c'est l'élastique qui la fait sentir. Les papiers visibles de la
 * bulle au centre comptent comme les bulles (retour d'Igor du 2026-10-20, chantier
 * post-it-et-listes, ticket 03) : un papier rangé loin se retrouve en glissant la carte.
 */
import type { Boite } from './placeDesEtiquettes';

export interface Camera {
  /** Où tombe le titre de la feuille, en pixels de la surface. */
  x: number;
  y: number;
  /** L'échelle. 1 : la taille des planches, « 100 % ». */
  s: number;
}

export interface Taille {
  largeur: number;
  hauteur: number;
}

/**
 * Le passage de l'écran à la carte : ce qui est sous le point (en pixels de la surface), dans le
 * repère du titre, selon le regard et le zoom. Écrit une fois (chantier post-it-et-listes,
 * ticket 03) : la molette zoome autour de lui, et un papier glissé le suit.
 */
export function pointDeLaCarte(camera: Camera, point: { x: number; y: number }): { x: number; y: number } {
  return { x: (point.x - camera.x) / camera.s, y: (point.y - camera.y) / camera.s };
}

/** Le rectangle qui contient toutes les bulles visibles, dans le repère du titre. */
export interface Contour {
  gauche: number;
  droite: number;
  haut: number;
  bas: number;
}

/**
 * Ce que les bulles gardent au moins dans la surface, en pixels d'écran : de quoi voir une
 * bulle et savoir d'où revenir (160 px, valeur reprise de l'ancienne maquette du dépliage).
 */
const MARGE_GARDEE = 160;
/** La résistance de l'élastique : 0,55, celle qu'on sent sur un téléphone. */
const RESISTANCE = 0.55;

export function contourDesBoites(boites: Boite[]): Contour {
  if (boites.length === 0) return { gauche: 0, droite: 0, haut: 0, bas: 0 };
  let gauche = Infinity;
  let droite = -Infinity;
  let haut = Infinity;
  let bas = -Infinity;
  for (const b of boites) {
    gauche = Math.min(gauche, b.x - b.demiLargeur);
    droite = Math.max(droite, b.x + b.demiLargeur);
    haut = Math.min(haut, b.y - b.demiHauteur);
    bas = Math.max(bas, b.y + b.demiHauteur);
  }
  return { gauche, droite, haut, bas };
}

/**
 * Sur un axe, où peut tomber le titre pour que le contour, de `debut` à `fin`, garde au moins
 * la marge dans une surface de longueur `cote`. Jamais vide : la marge ne dépasse pas la
 * moitié de la surface.
 */
function intervalle(debut: number, fin: number, s: number, cote: number): [number, number] {
  const marge = Math.min(MARGE_GARDEE, cote / 2);
  return [marge - fin * s, cote - marge - debut * s];
}

function dansIntervalle(v: number, [min, max]: [number, number]): number {
  return Math.min(Math.max(v, min), max);
}

/** Autour de ce que la caméra cadre, de quoi respirer, en pixels d'écran. */
const MARGE_DU_CADRE = 40;
/** En haut, de quoi laisser la barre des angles (Carte, Liste…) posée sur la carte. */
const HAUT_DU_CADRE = 110;

/**
 * La caméra qui cadre un contour (Regrouper, chantier theme-analyse, ticket 03) : tout le contour
 * à l'écran, centré en largeur, son haut sous la barre des angles (les titres des colonnes en haut,
 * comme la maquette `regroupe`), à l'échelle la plus grande qui le fait tenir, dans les bornes. S'il
 * est trop haut pour tenir au plancher, on glisse pour voir la suite.
 */
export function cameraQuiCadre(contour: Contour, ecran: Taille, bornes: { min: number; max: number }): Camera {
  const largeur = contour.droite - contour.gauche;
  const hauteur = contour.bas - contour.haut;
  const tient = Math.min(
    (ecran.largeur - 2 * MARGE_DU_CADRE) / Math.max(largeur, 1),
    (ecran.hauteur - HAUT_DU_CADRE - MARGE_DU_CADRE) / Math.max(hauteur, 1)
  );
  const s = Math.min(bornes.max, Math.max(bornes.min, tient));
  const x = ecran.largeur / 2 - ((contour.gauche + contour.droite) / 2) * s;
  return { x, y: HAUT_DU_CADRE - contour.haut * s, s };
}

/** La caméra la plus proche qui respecte la limite. La même si elle la respecte déjà. */
export function cameraBornee(camera: Camera, contour: Contour, ecran: Taille): Camera {
  const x = dansIntervalle(camera.x, intervalle(contour.gauche, contour.droite, camera.s, ecran.largeur));
  const y = dansIntervalle(camera.y, intervalle(contour.haut, contour.bas, camera.s, ecran.hauteur));
  return x === camera.x && y === camera.y ? camera : { ...camera, x, y };
}

/**
 * Au-delà de la limite, la main tire plus qu'elle ne déplace : un dépassement `d` n'en montre
 * que `(1 − 1 / (d·0,55 / L + 1))·L`, qui part à un peu plus de la moitié et ne dépasse jamais
 * la longueur `L` de la surface.
 */
function resister(d: number, cote: number): number {
  return (1 - 1 / ((d * RESISTANCE) / cote + 1)) * cote;
}

function elastique(v: number, [min, max]: [number, number], cote: number): number {
  if (v < min) return min - resister(min - v, cote);
  if (v > max) return max + resister(v - max, cote);
  return v;
}

/** Pendant un glisser : là où la main voudrait mettre la caméra, retenue par l'élastique. */
export function cameraTiree(voulue: Camera, contour: Contour, ecran: Taille): Camera {
  return {
    ...voulue,
    x: elastique(voulue.x, intervalle(contour.gauche, contour.droite, voulue.s, ecran.largeur), ecran.largeur),
    y: elastique(voulue.y, intervalle(contour.haut, contour.bas, voulue.s, ecran.hauteur), ecran.hauteur),
  };
}
